import {TempParameter} from './temp-parameter';
import {AdvancedFieldFunctionExecutor} from './advanced-field-function-executor';
import {set} from 'lodash';
import {ExtendedDynamicComponentList} from './conditional-components.interface';
import {CompositeKeyType} from '@/interfaces/key.interface';
import {BehaviorSubject, distinctUntilChanged} from 'rxjs';
import {AxiosResponse} from 'axios';

/**
 * Very abstract definition of a Field Function Configuration.
 *
 * Takes a configuration from the API
 * Based on api configuration
 * - it will trigger event calls (e.g. remote triggers)
 * - it will open a datatable for selection purpose, like the old search component (not implemented yet)
 * - it will perform rule execution (not implemented yet)
 *
 * In order to work with this piece, there are a few things mandatory.
 *
 * 1. initialize it by calling the constuctor using a predefined api configuration
 * 2. for each field function, call
 *    - bindNewFieldFunctionExecutor
 * 4. for each field definition inside each field function, call
 *    - bindNewFieldStateExecutor for advanced fields
 *    - bindNewStaticContextField for contextual fields
 * 3. call afterInit() so that the INIT event can be triggered
 */
export class AdvancedFieldFunctionConfiguration {
    private readonly advancedFieldFunctionConfiguration: DisguiseDto.AdvancedFieldFunctionConfiguration;
    private readonly tempParameter: { [uniqueTempParameterName: string]: TempParameter } = {};
    private readonly conditionalComponentDefinition: ExtendedDynamicComponentList;
    private readonly fieldFunctionExecutors: AdvancedFieldFunctionExecutor[] = [];
    private readonly contextParamUpdateCb: (patchableContextParams: CompositeKeyType) => void;
    private readonly nativeConditionalComponentEventCb: (
        nativeActionConfiguration: DisguiseDto.INativeEventConfigurationUnion,
        eventData: any
    ) => void;
    private readonly nativeErrorHandlingCb: (errorResponse: AxiosResponse) => void;

    private readonly pendingRequestCountChangedSubject = new BehaviorSubject<number>(0);
    private readonly pendingRequestCountChangedCb: (count: number) => void;
    private pendingRequestCount: number = 0;

    constructor(
        advancedFieldFunctionConfiguration: DisguiseDto.AdvancedFieldFunctionConfiguration,
        conditionalComponentDefinition: ExtendedDynamicComponentList,
        contextParamUpdateCb: (patchableContextParams: CompositeKeyType) => void,
        nativeConditionalComponentEventCb: (
            nativeActionConfiguration: DisguiseDto.INativeEventConfigurationUnion,
            eventData: any
        ) => void,
        nativeErrorHandlingCb: (errorResponse: AxiosResponse) => void,
        pendingRequestCountChangedCb: (count: number) => void
    ) {
        this.advancedFieldFunctionConfiguration = advancedFieldFunctionConfiguration;
        this.conditionalComponentDefinition = conditionalComponentDefinition;
        this.contextParamUpdateCb = contextParamUpdateCb;
        this.nativeConditionalComponentEventCb = nativeConditionalComponentEventCb;
        this.nativeErrorHandlingCb = nativeErrorHandlingCb;
        this.pendingRequestCountChangedCb = pendingRequestCountChangedCb;

        for (const currentTempParameter of this.advancedFieldFunctionConfiguration.tempParameters || []) {
            let tempParameterValue;
            if (currentTempParameter.value !== undefined) {
                tempParameterValue = currentTempParameter.value;
            } else {
                tempParameterValue = '';
            }
            this.tempParameter[currentTempParameter.uniqueTempParameterName || ''] = new TempParameter(
                currentTempParameter,
                tempParameterValue
            );
        }

        this.pendingRequestCountChangedSubject.pipe(
            distinctUntilChanged() // remove duplicates
        ).subscribe((reqCount) => {
            pendingRequestCountChangedCb(reqCount);
        });
    }

    bindNewFieldFunctionExecutor(
        advancedFieldFunctionComposition: DisguiseDto.AdvancedFieldFunctionComposition
    ) {
        const advancedFieldFunctionExecutor = new AdvancedFieldFunctionExecutor(
            advancedFieldFunctionComposition,
            this.conditionalComponentDefinition,
            this.tempParameter,
            (patchableContextParams: CompositeKeyType) => {
                this.contextParamUpdateCb(patchableContextParams);
            },
            (
                nativeActionConfiguration: DisguiseDto.INativeEventConfigurationUnion,
                eventData: any
            ) => {
                this.nativeConditionalComponentEventCb(
                    nativeActionConfiguration,
                    eventData
                );
            },
            (
                errorResponse: AxiosResponse
            ) => {
                this.nativeErrorHandlingCb(errorResponse);
            },
            () => {
                this.increasePendingRequestCount();
            },
            () => {
                this.decreasePendingRequestCount();
            }
        );
        this.fieldFunctionExecutors.push(advancedFieldFunctionExecutor);
        return advancedFieldFunctionExecutor;
    }

    getOperationParameters(icon: DisguiseDto.DisguiseIcon) {
        const result: { [key: string]: any } = {};
        if (icon) {
            const operationParams = this.advancedFieldFunctionConfiguration.operationParameters || {};
            for (const currentParamDescription of operationParams[icon] || []) {
                const currentParam = this.tempParameter[
                currentParamDescription.tempParameterUsageDescription?.uniqueTempParameterName || ''
                    ];
                if (!currentParam) {
                    throw new Error('Temp param not found. '
                        + currentParamDescription.tempParameterUsageDescription?.uniqueTempParameterName);
                }
                set(result,
                    currentParamDescription.tempParameterUsageDescription?.endpointKeyPath || '',
                    currentParam.getModelValue()
                );
            }
        }
        return result;
    }

    afterInit() {
        setTimeout(() => {
            this.conditionalComponentDefinition.componentsByIdentifier = {};
            for (const currentFieldRefExecutor of this.fieldFunctionExecutors) {
                currentFieldRefExecutor.afterInit();
            }
        });
    }

    afterReInit() {
        this.conditionalComponentDefinition.componentsByIdentifier = {};
        for (const currentFieldRefExecutor of this.fieldFunctionExecutors) {
            currentFieldRefExecutor.afterReInit();
        }
    }

    afterSave() {
        this.conditionalComponentDefinition.componentsByIdentifier = {};
    }

    /**
     * Destroys the AFFE
     */
    destroy() {
        for (const currentFieldExecutors of this.fieldFunctionExecutors || []) {
            currentFieldExecutors.destroy();
        }
        this.conditionalComponentDefinition.componentsByIdentifier = {};
    }

    private increasePendingRequestCount() {
        this.pendingRequestCountChangedSubject.next(++this.pendingRequestCount);
    }

    private decreasePendingRequestCount() {
        this.pendingRequestCountChangedSubject.next(--this.pendingRequestCount);
    }
}
